import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardHeader, CardContent, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { Brain, LogIn } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (!res.ok) {
      toast.error(res.error);
      return;
    }
    toast.success(`Bienvenido, ${res.user.name}`);
    const target = res.user.role === "patient" ? "/dashboard"
      : res.user.role === "admin" ? "/admin/users"
      : "/pro";
    nav(target);
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-primary text-primary-foreground">
        <div className="bg-primary-foreground rounded-2xl px-4 py-3 inline-flex self-start">
          <Logo size="md" testid="login-brand-logo" />
        </div>
        <div>
          <h1 className="heading text-4xl font-extrabold leading-tight">
            Programa de entrenamiento<br />en cognición social.
          </h1>
          <p className="mt-2 text-primary-foreground/70 text-sm italic">
            Digital platform for rehabilitation of social cognition
          </p>
          <p className="mt-4 text-primary-foreground/85 text-lg max-w-md">
            Accede a tus sesiones, evaluaciones y perfil cognitivo personalizado.
          </p>
        </div>
        <div className="text-sm opacity-75">© 2026 SocialCog Training®</div>
      </div>

      <div className="flex items-center justify-center px-6 py-16 bg-background">
        <Card className="w-full max-w-md border-border rounded-2xl">
          <CardHeader>
            <CardTitle className="heading text-2xl">Iniciar sesión</CardTitle>
            <CardDescription>
              ¿Aún no tienes cuenta? <Link to="/register" className="underline text-primary" data-testid="link-register">Regístrate</Link>
            </CardDescription>
          </CardHeader>
          <form onSubmit={submit}>
            <CardContent className="space-y-5">
              <div>
                <Label htmlFor="email">Correo electrónico</Label>
                <Input id="email" type="email" required value={email}
                  onChange={(e) => setEmail(e.target.value)} className="mt-2 h-11" data-testid="login-email-input" />
              </div>
              <div>
                <Label htmlFor="password">Contraseña</Label>
                <Input id="password" type="password" required value={password}
                  onChange={(e) => setPassword(e.target.value)} className="mt-2 h-11" data-testid="login-password-input" />
              </div>
              <details className="text-sm text-muted-foreground bg-secondary rounded-lg px-3 py-2">
                <summary className="cursor-pointer font-medium">Cuentas demo</summary>
                <ul className="mt-2 space-y-1 font-mono">
                  <li>paciente@socialcog.app / Paciente1!</li>
                  <li>pro@socialcog.app / Pro12345!</li>
                </ul>
              </details>
            </CardContent>
            <CardFooter className="flex flex-col gap-3">
              <Button type="submit" disabled={loading} className="w-full h-12 text-base rounded-full" data-testid="login-submit-btn">
                <LogIn size={18} /> {loading ? "Entrando..." : "Entrar"}
              </Button>
            </CardFooter>
          </form>
        </Card>
      </div>
    </div>
  );
}
